"use client";

import { useFeatureFlagVariantKey } from "posthog-js/react";
import { useEffect, useRef, useState } from "react";
import { useSession } from "@/lib/auth/client";
import {
	CLIENT_RESOLVE_TIMEOUT_MS,
	fetchJsonOrFallback,
} from "@/lib/net/fetch-with-timeout";
import {
	__resetNavbarDataCacheForTest,
	fetchNavbarData,
} from "@/lib/user/navbar-data-client";
import { type AnchoredSurface, resolveSurfaceAnchor } from "./anchors";
import {
	canShowUnit,
	type GuardrailDecision,
	type UnitKind,
} from "./guardrails";
import { resolveSegment, shallowSegmentFor, type Tier } from "./segment";
import type { MonetizationSettings, Segment } from "./settings";
import { DEFAULT_SETTINGS } from "./settings-defaults";

/**
 * Client-side "aggressive but not annoying" gate for a monetized unit.
 *
 * Enforces the (previously dead) guardrail layer at the point of render, where
 * the session state actually lives:
 *  - Pro-respect: a paying member sees NO affiliate unit (ad-free is the perk).
 *  - Session frequency caps: per-segment `sessionImpressionCap` / `maxAffiliate`
 *    via the pure `canShowUnit`. The `aggression` profile is fetched from
 *    `/api/monetization/guardrails` (resolved through the `monetization-settings`
 *    PostHog payload, same as payouts/surfaces) — so it's tunable live, no deploy.
 *  - Reserved budget: a surface listed in `reservedAffiliateSurfaces` gets its own
 *    session counter instead of competing with every other surface for the
 *    shared `maxAffiliate` pool (see settings.ts for why — job_detail_after_apply
 *    was starved 100% of the time by job_sidebar resolving first).
 *  - Dismiss: a unit the viewer actively rejected stays gone for the session.
 *
 * Counts + dismissals live in `sessionStorage` so they survive within-session
 * navigation but reset per session (a fresh visit starts clean). The decision is
 * reported to telemetry (impression suppressed + why) so we can measure how often
 * the caps bite and tune the dial — the server still records real impressions via
 * `AffiliateImpression`, this only governs whether a unit is allowed to render.
 *
 * Tier: anon (no session) is definitely not Pro → decided immediately with no
 * fetch. A logged-in viewer's plan is confirmed once via `/api/user/navbar-data`
 * (shared across all units on the page by a module-level cache, so N ad slots do
 * NOT trigger N requests).
 */

// A/B test (2026-09-08): job_sidebar's affiliate_cap suppression rate hit 41%
// of slot-resolution attempts over 14d (query in
// docs/plans backlog / project memory), rising, while anon_shallow sat on
// maxAffiliate:2/sessionImpressionCap:3 — a budget sized for lighter pages,
// not a session that views 3+ job listings. `treatment` roughly doubles the
// anon_shallow budget; `control` is a no-op (falls through to the fetched
// config unchanged). Scoped to this one segment so it can't leak into other
// tiers/surfaces. Kill by archiving the flag in PostHog — no deploy needed.
const AFFILIATE_CAP_EXPERIMENT_FLAG = "exp-job-sidebar-affiliate-cap";
const AFFILIATE_CAP_EXPERIMENT_SEGMENT = "anon_shallow" as const;
const AFFILIATE_CAP_EXPERIMENT_OVERRIDE = {
	maxAffiliate: 4,
	sessionImpressionCap: 5,
} as const;

const COUNTS_KEY = "ld_ad_counts";
const RESERVED_COUNTS_KEY = "ld_ad_reserved_counts";
const DISMISSED_KEY = "ld_ad_dismissed";

type Counts = { display: number; affiliate: number };
type ReservedCounts = Record<string, number>;

function readCounts(): Counts {
	if (typeof sessionStorage === "undefined")
		return { display: 0, affiliate: 0 };
	try {
		const raw = sessionStorage.getItem(COUNTS_KEY);
		if (!raw) return { display: 0, affiliate: 0 };
		const p = JSON.parse(raw) as Partial<Counts>;
		return {
			display: typeof p.display === "number" ? p.display : 0,
			affiliate: typeof p.affiliate === "number" ? p.affiliate : 0,
		};
	} catch {
		return { display: 0, affiliate: 0 };
	}
}

function writeCounts(c: Counts): void {
	if (typeof sessionStorage === "undefined") return;
	try {
		sessionStorage.setItem(COUNTS_KEY, JSON.stringify(c));
	} catch {
		/* storage full / disabled — degrade to no cap, never throw */
	}
}

function readReservedCounts(): ReservedCounts {
	if (typeof sessionStorage === "undefined") return {};
	try {
		const raw = sessionStorage.getItem(RESERVED_COUNTS_KEY);
		if (!raw) return {};
		const p = JSON.parse(raw) as unknown;
		if (typeof p !== "object" || p === null) return {};
		const out: ReservedCounts = {};
		for (const [k, v] of Object.entries(p as Record<string, unknown>)) {
			if (typeof v === "number") out[k] = v;
		}
		return out;
	} catch {
		return {};
	}
}

function writeReservedCounts(c: ReservedCounts): void {
	if (typeof sessionStorage === "undefined") return;
	try {
		sessionStorage.setItem(RESERVED_COUNTS_KEY, JSON.stringify(c));
	} catch {
		/* storage full / disabled — degrade to no cap, never throw */
	}
}

function readDismissed(): Set<string> {
	if (typeof sessionStorage === "undefined") return new Set();
	try {
		const raw = sessionStorage.getItem(DISMISSED_KEY);
		if (!raw) return new Set();
		const arr = JSON.parse(raw) as unknown;
		return Array.isArray(arr)
			? new Set(arr.filter((s): s is string => typeof s === "string"))
			: new Set();
	} catch {
		return new Set();
	}
}

function addDismissed(provider: string): void {
	if (typeof sessionStorage === "undefined") return;
	try {
		const set = readDismissed();
		set.add(provider);
		sessionStorage.setItem(DISMISSED_KEY, JSON.stringify([...set]));
	} catch {
		/* ignore */
	}
}

// Pro status shares the page-wide `/api/user/navbar-data` cache (see
// `@/lib/user/navbar-data-client`) — NavbarAuth and AdSlotClient's pro-upsell
// suppression read the same in-flight promise, so N ad units + the navbar
// still fire ONE request total, not one per consumer.
function fetchIsPro(): Promise<boolean> {
	return fetchNavbarData().then((d) => d?.isPro === true);
}

/** Reset the shared Pro cache — test-only. */
export function __resetProCacheForTest(): void {
	__resetNavbarDataCacheForTest();
}

type GuardrailConfig = {
	aggression: MonetizationSettings["aggression"];
	reservedAffiliateSurfaces: MonetizationSettings["reservedAffiliateSurfaces"];
	surfaces: MonetizationSettings["surfaces"];
	adsense: MonetizationSettings["adsense"];
};

const FALLBACK_CONFIG: GuardrailConfig = {
	aggression: DEFAULT_SETTINGS.aggression,
	reservedAffiliateSurfaces: DEFAULT_SETTINGS.reservedAffiliateSurfaces,
	surfaces: DEFAULT_SETTINGS.surfaces,
	adsense: DEFAULT_SETTINGS.adsense,
};

// Module-level cache so every ad unit on a page shares ONE guardrail-config
// fetch. `/api/monetization/guardrails` resolves the PostHog payload
// server-side (force-static, 60s ISR) — a failed/unreachable/SLOW fetch
// degrades to the code-default floor, never blocks ad rendering.
//
// "slow" is not a flourish (2026-08-21): a production trace on a real guest
// browser measured this exact request at 79,180ms — not because the route is
// slow (0.33–0.51s warm, server-side) but because it sat behind a third-party
// widget's 32-request cascade. A hang is not a rejection, so the old
// `.catch()` never ran and the promise every ad unit on the page awaits simply
// never settled. `fetchJsonOrFallback` puts a deadline on it.
let configPromise: Promise<GuardrailConfig> | null = null;

/**
 * Session-scoped cache for the guardrail config.
 *
 * ── Why the caching has to happen HERE ────────────────────────────────────
 * `/api/monetization/guardrails` is `force-static` + `revalidate = 60` and is
 * genuinely fast server-side (0.33–0.51s warm, `x-nextjs-cache: HIT`). What it
 * is NOT is edge-cached: a live header dump shows no `cf-cache-status` at all,
 * so every page view costs a Worker invocation against the 30M CPU-ms budget.
 *
 * Three edge-side fixes were investigated first and all are closed (2026-08-22):
 *
 *  1. A zone Cache Rule. Next sends `Vary: rsc, next-router-state-tree,
 *     next-router-prefetch, next-router-segment-prefetch` on this response, and
 *     a Cache Rule cannot override that `Vary` exclusion — verified a no-op on
 *     this zone on 2026-06-13 and written up in
 *     `src/lib/cache/edge-html-cache.ts`. The zone has no cache ruleset at all.
 *  2. Stripping the `Vary` in middleware. `src/middleware.ts`'s matcher
 *     excludes `/api`, so middleware never sees this route.
 *  3. The in-Worker Cache API tier that solves exactly this for HTML
 *     (`serveWithEdgeCache`). It reports `x-edge-cache: bypass` here because
 *     the route is not an HTML path.
 *
 * (3) is now covered: `src/lib/cache/edge-json-cache.ts` gives the route its
 * own colo-local tier in `src/worker.ts`, which runs before Next and is not
 * bound by the middleware matcher, so it can drop the `Vary` the CDN chokes on.
 * This session cache still earns its place above it — a Cache API hit is
 * colo-local but still a Worker invocation, and this makes repeat page views in
 * a session cost no request at all.
 *
 * That leaves the request count itself, which is the honest lever: the config
 * is `Cache-Control: public` (identical for every visitor) and changes at most
 * once a minute, yet a full page load re-fetches it because `configPromise`
 * lives only as long as the document. Persisting it for the same 60s the
 * server uses makes repeat page views in a session cost zero requests.
 *
 * `sessionStorage`, not `localStorage`: the TTL is short, the value is not
 * worth carrying across sessions, and this file already uses sessionStorage
 * for the impression counters next to it.
 */
export const GUARDRAIL_CACHE_KEY = "ld_guardrail_cfg_v1";

/** Matches the route's own `revalidate = 60` — never cache client-side for
 * longer than the server's own cadence, or a flag flip in PostHog takes longer
 * to reach a real browser than the dashboard implies. */
export const GUARDRAIL_CACHE_TTL_MS = 60_000;

type CachedConfigEnvelope = { at: number; cfg: GuardrailConfig };

function readCachedConfig(): GuardrailConfig | null {
	if (typeof sessionStorage === "undefined") return null;
	try {
		const raw = sessionStorage.getItem(GUARDRAIL_CACHE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as Partial<CachedConfigEnvelope>;
		if (typeof parsed?.at !== "number" || !parsed.cfg) return null;
		const age = Date.now() - parsed.at;
		// `age < 0` guards a clock that moved backwards (a laptop waking, a
		// timezone/NTP correction): treat it as a miss rather than trusting an
		// entry that claims to be from the future.
		if (age < 0 || age > GUARDRAIL_CACHE_TTL_MS) return null;
		const cfg = parsed.cfg;
		// Shape check: a partially-written or schema-drifted entry must degrade
		// to a refetch, never render against half a config.
		// `adsense` was added 2026-09-21: an entry cached by a pre-deploy tab
		// lacks it and must refetch rather than read the overrides as absent.
		if (
			!cfg.aggression ||
			!cfg.surfaces ||
			!cfg.reservedAffiliateSurfaces ||
			!cfg.adsense
		) {
			return null;
		}
		return cfg;
	} catch {
		// Unparseable / storage disabled — never throw on a read path.
		return null;
	}
}

function writeCachedConfig(cfg: GuardrailConfig): void {
	if (typeof sessionStorage === "undefined") return;
	try {
		const envelope: CachedConfigEnvelope = { at: Date.now(), cfg };
		sessionStorage.setItem(GUARDRAIL_CACHE_KEY, JSON.stringify(envelope));
	} catch {
		/* storage full / private mode — the fetch already succeeded, so this is
		   a missed optimisation, not a failure. */
	}
}
/** Exported for `useResolvedAnchor` (placement anchors, PR-5) — shares this
 * same cached fetch rather than opening a second request for `surfaces`. */
export function fetchGuardrailConfig(): Promise<GuardrailConfig> {
	if (!configPromise) {
		const cached = readCachedConfig();
		if (cached) {
			configPromise = Promise.resolve(cached);
			return configPromise;
		}
		configPromise = fetchJsonOrFallback<Partial<GuardrailConfig> | null>(
			"/api/monetization/guardrails",
			null,
		).then((d) => {
			const config: GuardrailConfig = {
				aggression: d?.aggression ?? FALLBACK_CONFIG.aggression,
				reservedAffiliateSurfaces:
					d?.reservedAffiliateSurfaces ??
					FALLBACK_CONFIG.reservedAffiliateSurfaces,
				surfaces: d?.surfaces ?? FALLBACK_CONFIG.surfaces,
				adsense: d?.adsense ?? FALLBACK_CONFIG.adsense,
			};
			// Only a REAL response is worth remembering. `d === null` means the
			// request failed or timed out and `config` is the code-default floor
			// — caching that would let one blip pin every surface to defaults for
			// the whole TTL, turning a momentary network fault into a minute of
			// silently untuned monetization.
			if (d) writeCachedConfig(config);
			return config;
		});
	}
	return configPromise;
}

/** Reset the shared guardrail-config cache — test-only. Clears BOTH tiers:
 * leaving the sessionStorage envelope behind would make the next test read a
 * previous test's config and pass for the wrong reason. */
export function __resetGuardrailConfigCacheForTest(): void {
	configPromise = null;
	try {
		sessionStorage?.removeItem(GUARDRAIL_CACHE_KEY);
	} catch {
		/* storage unavailable in this environment */
	}
}

export type AdGuardrails = {
	/** Null until tier + config resolve (avoid a flash before Pro is known). */
	ready: boolean;
	/** True when this unit must NOT render (pro / cap / dismissed). */
	blocked: boolean;
	blockReason?: "pro" | GuardrailDecision["reason"] | "dismissed";
	/** Call when the unit becomes viewable — increments the session affiliate count. */
	recordImpression: () => void;
	/** Hide + suppress this provider for the rest of the session. */
	dismiss: (provider: string) => void;
	/** Whether a given provider was already dismissed this session. */
	isDismissed: (provider: string) => boolean;
	/**
	 * Whether units may show a close (X) control for this viewer's segment —
	 * `aggression.<segment>.adsDismissible` from the monetization payload,
	 * true when absent. When false, callers render no X and ignore
	 * `isDismissed`, so a dismissal stored before the flip cannot hide a unit.
	 */
	dismissible: boolean;
	/** Resolved aggression segment (non-Pro, once ready) — the `segment`
	 * dimension of the Wave 4c targeting context. */
	segment?: Exclude<Segment, "pro">;
};

const NOT_READY: AdGuardrails = {
	ready: false,
	blocked: false,
	recordImpression: () => {},
	dismiss: () => {},
	isDismissed: () => false,
	dismissible: true,
};

const PRO_BLOCKED: AdGuardrails = {
	ready: true,
	blocked: true,
	blockReason: "pro",
	recordImpression: () => {},
	dismiss: () => {},
	isDismissed: () => false,
	dismissible: true,
};

/**
 * Decide whether one affiliate unit may render this session. Pure inputs
 * (sessionStorage + tier + fetched config) → a decision the caller renders on.
 *
 * @param surface Pass the caller's monetization surface (e.g. "job_sidebar",
 * "job_detail_after_apply") so a surface listed in `reservedAffiliateSurfaces`
 * gets its own budget instead of the shared `maxAffiliate` pool. Omit for
 * callers that don't need reserved-budget behavior — falls back to the shared
 * pool exactly as before.
 *
 * @param kind Which pool this unit draws from. Defaults to `"affiliate"`, which
 * is what every caller meant before `AdSlot`'s third-party display unit was
 * wired in — the hook used to hardcode it. A `"display"` caller is checked
 * against `maxDisplay` instead of `maxAffiliate` and, when it renders, bumps
 * `counts.display`. Both kinds share ONE `sessionImpressionCap`, and that shared
 * ceiling is the whole point: an Adsterra unit and a first-party affiliate card
 * are competing for the same viewer's attention on the same page, so they must
 * draw on the same session budget rather than each running an uncapped race.
 * `reserved` is affiliate-only — a reserved surface budget is a fix for
 * affiliate surfaces starving each other and has no display analogue.
 */
export function useAdGuardrails(
	surface?: string,
	kind: UnitKind = "affiliate",
): AdGuardrails {
	const { data: session, isPending } = useSession();
	const [tier, setTier] = useState<Tier | null>(null);
	const [config, setConfig] = useState<GuardrailConfig | null>(null);
	const recordedRef = useRef(false);
	const capExperimentVariant = useFeatureFlagVariantKey(
		AFFILIATE_CAP_EXPERIMENT_FLAG,
	);

	useEffect(() => {
		let cancelled = false;
		fetchGuardrailConfig().then((c) => {
			if (!cancelled) setConfig(c);
		});
		return () => {
			cancelled = true;
		};
	}, []);

	// Bounded session wait (2026-09-05). `useSession()` is an origin round-trip
	// through the Next bundle — 1–5 s on this app (docs/audit/
	// perf-baseline-2026-09-05.md: real-user TTFB p50 2.7 s, cold isolates
	// 19% of navigations) — and every unit on the page sat at `NOT_READY`
	// until it returned. A reader had scrolled past the slot before the hook
	// had even decided whether to draw. Past the deadline the viewer is
	// treated as anonymous: that is the right answer for the large majority,
	// and the wrong one only for a Pro member whose session answers late —
	// who is then blocked the moment it does (the `session?.user` dependency
	// below re-runs and `fetchIsPro` flips the tier), so the exposure is the
	// same brief window the flag-resolve timeout already accepts for the apply
	// CTA. Same constant, same reasoning: `CLIENT_RESOLVE_TIMEOUT_MS`.
	const [sessionWaitExpired, setSessionWaitExpired] = useState(false);
	useEffect(() => {
		if (!isPending) return;
		const timer = setTimeout(
			() => setSessionWaitExpired(true),
			CLIENT_RESOLVE_TIMEOUT_MS,
		);
		return () => clearTimeout(timer);
	}, [isPending]);

	useEffect(() => {
		if (isPending && !sessionWaitExpired) return;
		if (!session?.user) {
			setTier("anon");
			return;
		}
		let cancelled = false;
		fetchIsPro().then((isPro) => {
			if (!cancelled) setTier(isPro ? "pro" : "free");
		});
		return () => {
			cancelled = true;
		};
	}, [session?.user, isPending, sessionWaitExpired]);

	const ready = tier !== null && config !== null;
	if (!ready) return NOT_READY;

	// Pro is ad-free: never show a monetized unit of either kind.
	if (tier === "pro") return PRO_BLOCKED;

	const counts = readCounts();
	// Pass the tier's shallow profile so the promotion threshold is clamped to a
	// depth this viewer can actually reach. Without it a shallow profile whose
	// caps sum below `DEEP_DEPTH_THRESHOLD` strands its own deep segment — see
	// `deepDepthThresholdFor`.
	const segment = resolveSegment({
		tier,
		sessionDepth: counts.display + counts.affiliate,
		shallowProfile: config.aggression[shallowSegmentFor(tier)],
	});
	const profile =
		segment === AFFILIATE_CAP_EXPERIMENT_SEGMENT &&
		capExperimentVariant === "treatment"
			? { ...config.aggression[segment], ...AFFILIATE_CAP_EXPERIMENT_OVERRIDE }
			: config.aggression[segment];
	const reservedCap =
		kind === "affiliate" && surface
			? config.reservedAffiliateSurfaces[surface]
			: undefined;
	const reserved =
		reservedCap !== undefined && surface
			? { count: readReservedCounts()[surface] ?? 0, cap: reservedCap }
			: undefined;
	const decision = canShowUnit({
		profile,
		kind,
		counts,
		reserved,
	});

	return {
		ready: true,
		blocked: !decision.allowed,
		blockReason: decision.reason,
		recordImpression: () => {
			if (recordedRef.current) return;
			recordedRef.current = true;
			const c = readCounts();
			writeCounts({ ...c, [kind]: c[kind] + 1 });
			if (reservedCap !== undefined && surface) {
				const rc = readReservedCounts();
				writeReservedCounts({ ...rc, [surface]: (rc[surface] ?? 0) + 1 });
			}
		},
		dismiss: (provider: string) => addDismissed(provider),
		isDismissed: (provider: string) => readDismissed().has(provider),
		dismissible: profile.adsDismissible !== false,
		// `tier === "pro"` returned PRO_BLOCKED above, and `resolveSegment` only
		// yields "pro" for that tier — so `segment` is never "pro" here.
		segment: segment as Exclude<Segment, "pro">,
	};
}

/**
 * Placement anchors (PR-5). Resolves which single anchor id a registered
 * surface should render at, live-tunable via the `monetization-settings`
 * PostHog payload with NO deploy. Shares `fetchGuardrailConfig`'s cached
 * fetch (one `/api/monetization/guardrails` request per page, 60s ISR) —
 * cheap enough that the job-detail page's own `AnchorSlot`s (one per
 * candidate anchor) each call this independently and still settle to the
 * same resolved value.
 *
 * Starts OPTIMISTIC at the surface's default (first registered) anchor —
 * not `null`/loading — so the common case (no override configured) renders
 * immediately with zero extra layout shift versus the pre-PR-5 unconditional
 * mount. Only flips (and only for a deliberately-configured override) once
 * the fetch resolves.
 */
export function useResolvedAnchor(surface: AnchoredSurface): string {
	const [anchor, setAnchor] = useState<string>(() =>
		resolveSurfaceAnchor(FALLBACK_CONFIG.surfaces, surface),
	);
	useEffect(() => {
		let cancelled = false;
		fetchGuardrailConfig().then((config) => {
			if (!cancelled) setAnchor(resolveSurfaceAnchor(config.surfaces, surface));
		});
		return () => {
			cancelled = true;
		};
	}, [surface]);
	return anchor;
}
